import {NetworkStatus} from '@apollo/client';
import gql from 'graphql-tag';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {Permissions} from 'auth/PermissionConstants';
import {canRequestAccess, getArgForRequestAccess, requestAccess} from 'auth/RequestAccess';
import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import {ReviewLifecycleTerminalState, ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  CachedDataWhenRefetchingNamedOption,
  QueryResultType,
  useBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ReviewBadgeContextValue, ReviewError} from 'notebook/reviews/ReviewBadgeContext';
import {isReviewableEntry} from 'notebook/reviews/ReviewUtil';
import notifications, {GenericResponseJSON} from 'notifications';
import * as logging from 'util/Logging';
import {OngoingTaskResponse} from 'util/TaskRunner';

import {ReviewableQuery, ReviewableQueryVariables} from './__generated/ReviewableQuery';
import {deriveCyclicalReviewBadge} from './cyclicalReviewBadgeDisplayState';
import {CyclicalReviewPublishState, deriveCyclicalPublishState} from './cyclicalReviewPublishState';
import {deriveLinearReviewBadge} from './linearReviewBadgeDisplayState';
import {hasSelfReviewCriteriaConflict, type ReviewBadgeGatingInput} from './reviewBadgeShared';
import {
  PermissionsGraphQL,
  Review,
  ReviewAction,
  ReviewableGraphQL,
  ReviewableOrInaccessibleGraphQL,
  ReviewBadgeStatus,
} from './ReviewConstants';

export const REVIEWABLE_QUERY = gql`
  query ReviewableQuery($reviewableId: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    reviewable(id: $reviewableId) {
      ... on ReviewableInterface {
        id
        review {
          ...ReviewLifecycleReviewSummaryGraphQL
          activeReviewLifecycleVersion {
            ... on ReviewLifecycleVersionNode {
              id
              terminalState
            }
          }
        }
        parentReviewLifecycles {
          id
          currentVersion {
            id
            lifecycleType
            reviewStages {
              id
              action
            }
          }
        }
        reviewSource {
          id
        }
      }
      ... on EntryNode {
        id
        hasReviewCriteria
        template {
          id
          templateCollection {
            id
          }
        }
      }
      ... on HasPermissionsInterface {
        permissions {
          admin
          appendable
          readable
          writable
          canAccept
          canReassignRlcReviewer
          canReject
          canRetract
          canSendForReview
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ReviewLifecycleReviewSummary').fragment}
`;

// TODO (BNCH-112863) This wrapper forces the ReviewBadge benchling query to re-run after a review change.
// The function was added because the Apollo GraphQL 3.4.9 -> 3.11 upgrade is more lenient on caching,
// which causes the review badge info to not necessarily get correctly updated. Explicitly refetching
// after a review change matches the 3.4.9 behavior until the logical flow can be revisited and maybe
// redesigned.
//
// The review action has already succeeded by the time the returned callback runs, so onReviewChange must
// fire regardless of whether the badge refetch succeeds (otherwise a refetch failure would leave parent
// state/UI stale). A failed refetch is logged rather than left to surface as an unhandled rejection.
export function wrapOnReviewChangeWithRefetch(
  refetch: () => Promise<unknown>,
  onReviewChange:
    | ((
        reviewableId: string,
        review: Review,
        filePermissions: Permissions,
        snapshotTask: OngoingTaskResponse | null
      ) => void | Promise<void>)
    | undefined
): (
  reviewableId: string,
  review: Review,
  filePermissions: Permissions,
  snapshotTask: OngoingTaskResponse | null
) => void {
  return function (
    reviewableId: string,
    review: Review,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ): void {
    refetch()
      .catch((error: unknown) => {
        logging.error(error instanceof Error ? error : 'Failed to refetch reviewable after review change');
      })
      .then(() => onReviewChange?.(reviewableId, review, filePermissions, snapshotTask))
      .catch((error: unknown) => {
        logging.error(error instanceof Error ? error : 'Failed to apply review update after review change');
      });
  };
}

function handleInaccessible(reviewable: ReviewableOrInaccessibleGraphQL): void {
  const argForRequestAccess = getArgForRequestAccess(reviewable);
  if (argForRequestAccess) {
    if (canRequestAccess(argForRequestAccess)) {
      requestAccess(argForRequestAccess);
    } else {
      notifications.error(FileBrowserConstants.INACCESSIBLE_MESSAGE);
    }
  }
}

/** The behavioral (non-presentation) props a consuming surface supplies to drive review actions. */
export interface ReviewControllerDomainAdapter {
  enableReviewLifecycle?: boolean;
  onReviewChange?: (
    reviewableId: string,
    review: Review,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void | Promise<void>;
  openHistory?: () => void;
  renderAdditionalReviewInfo?: (reviewableId: string, reviewAction: ReviewAction) => JSX.Element | null;
  renderReviewError?: (error: {responseJSON?: GenericResponseJSON}) => JSX.Element | null;
  getSendForReviewValidationError?: (reviewableId: string) => string | null;
  performanceMetadata?: {[key: string]: string};
  unsubmittedTableNames?: Array<string>;
  /** When set, derive badge state from reviewChanges scoped to this version (cyclical/template reviews). */
  openVersionId?: string;
  /** Whether the open version is the latest editable draft (no publishing record). Cyclical only. */
  isCurrentEditableDraft?: boolean;
  /** When set with openVersionId, derive template publish gating from the same reviewable query. */
  cyclicalPublishGating?: {
    isLatest: boolean;
    isPublishOptionDisabledFromParent?: boolean;
  };
}

export type ReviewControllerHiddenReason = 'query_failed' | 'inaccessible' | 'badge_hidden';

interface ReviewControllerHiddenState {
  state: 'HIDDEN';
  reason: ReviewControllerHiddenReason;
}

interface ReviewControllerReadyState {
  state: 'READY';
  reviewStatus: ReviewStatus | undefined;
  terminalState: ReviewLifecycleTerminalState | null | undefined;
  badgeStatus: ReviewBadgeStatus;
  isDropdown: boolean;
  showSendForReview: boolean;
  showSummaryPanel: boolean;
  permissions: PermissionsGraphQL;
  contextValue: ReviewBadgeContextValue;
  dropdown: {
    isOpen: boolean;
    onToggle: (isOpen: boolean) => void;
    disableOptions: boolean;
    tooltipTitle: string | null;
  };
  realtime: {apiId: string; refetch: () => Promise<void>};
}

export type ReviewControllerResult =
  | {state: 'PENDING'; publishState?: CyclicalReviewPublishState}
  | (ReviewControllerHiddenState & {publishState?: CyclicalReviewPublishState})
  | (ReviewControllerReadyState & {publishState?: CyclicalReviewPublishState});

/**
 * Headless controller for the review badge/panel. Owns the reviewable query, derivation (see
 * linearReviewBadgeDisplayState / cyclicalReviewBadgeDisplayState), open-time send-for-review validation, the realtime refetch contract
 * (BNCH-112863), and the ReviewBadgeContext value that bridges into the dropdown's detached render tree.
 *
 * The return value is intentionally data-first (status, gating flags, permissions, context, dropdown
 * state) rather than presentation: text/icon/style mapping stays in the consuming view so a future
 * redesign can render its own UI while reusing this controller.
 */
export function useReviewController(
  reviewableId: string,
  domainAdapter: ReviewControllerDomainAdapter
): ReviewControllerResult {
  const {
    enableReviewLifecycle,
    onReviewChange,
    openHistory,
    renderAdditionalReviewInfo,
    renderReviewError,
    getSendForReviewValidationError,
    performanceMetadata,
    unsubmittedTableNames,
    openVersionId,
    isCurrentEditableDraft = false,
    cyclicalPublishGating,
  } = domainAdapter;

  const result = useBenchlingQuery<ReviewableQuery, ReviewableQueryVariables>(REVIEWABLE_QUERY, {
    variables: {reviewableId},
    fetchPolicy: 'cache-and-network',
    renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
  });

  const refetch = result.refetch;
  const [isRefetchingAfterReviewChange, setIsRefetchingAfterReviewChange] = useState(false);

  const refetchCallback = useCallback(
    async (afterReviewChange = false): Promise<void> => {
      if (afterReviewChange) {
        setIsRefetchingAfterReviewChange(true);
      }
      try {
        await refetch({reviewableId});
      } finally {
        if (afterReviewChange) {
          setIsRefetchingAfterReviewChange(false);
        }
      }
    },
    [refetch, reviewableId]
  );

  const onReviewChangeWrapped = useMemo(
    () => wrapOnReviewChangeWithRefetch(() => refetchCallback(true), onReviewChange),
    [refetchCallback, onReviewChange]
  );

  const {reviewable} = result.type === QueryResultType.SUCCESS ? result.data : {reviewable: undefined};
  const cachedReviewableId = reviewable != null ? getAccessibleOrInaccessibleId(reviewable) : undefined;
  const hasMismatchedCachedReviewable = cachedReviewableId != null && cachedReviewableId !== reviewableId;
  const isServingStaleCachedReviewable =
    result.type === QueryResultType.SUCCESS &&
    (result.loading ||
      result.networkStatus === NetworkStatus.refetch ||
      result.networkStatus === NetworkStatus.setVariables);

  useEffect(() => {
    if (hasMismatchedCachedReviewable) {
      return;
    }
    if (reviewable != null && isInaccessible(reviewable)) {
      handleInaccessible(reviewable);
    }
  }, [reviewable, hasMismatchedCachedReviewable]);

  const accessibleReviewable =
    reviewable != null && !isInaccessible(reviewable) ? (reviewable as ReviewableGraphQL) : undefined;

  const review = accessibleReviewable?.review;
  const reviewId = review?.id;
  const parentReviewLifecycles = accessibleReviewable?.parentReviewLifecycles ?? [];
  const hasReviewCriteria =
    accessibleReviewable != null && isReviewableEntry(accessibleReviewable)
      ? accessibleReviewable.hasReviewCriteria
      : false;
  const reviewUsesReviewLifecycle = review?.reviewLifecycle != null;

  const hasCriteriaConflict = hasSelfReviewCriteriaConflict({
    hasReviewCriteria,
    projectLifecycleTypes: parentReviewLifecycles.map(({currentVersion}) => currentVersion.lifecycleType),
  });
  const reviewError: ReviewError = useMemo(
    () => ({
      hasSelfReviewCriteriaConflict: hasCriteriaConflict,
      sendForReviewValidationError: getSendForReviewValidationError,
      renderReviewError,
    }),
    [hasCriteriaConflict, getSendForReviewValidationError, renderReviewError]
  );

  const badgeGatingInput = useMemo(
    (): ReviewBadgeGatingInput => ({
      enableReviewLifecycle,
      canSendForReview: accessibleReviewable?.permissions.canSendForReview ?? null,
      parentReviewLifecycleCount: parentReviewLifecycles.length,
      reviewUsesReviewLifecycle,
    }),
    [
      enableReviewLifecycle,
      accessibleReviewable?.permissions.canSendForReview,
      parentReviewLifecycles.length,
      reviewUsesReviewLifecycle,
    ]
  );

  const badgeDerivation = useMemo(
    () =>
      openVersionId != null
        ? deriveCyclicalReviewBadge({
            ...badgeGatingInput,
            openVersionId,
            isCurrentEditableDraft,
            reviewChanges: review?.reviewChanges ?? [],
          })
        : deriveLinearReviewBadge({
            ...badgeGatingInput,
            reviewStatus: review?.status,
          }),
    [openVersionId, isCurrentEditableDraft, review?.reviewChanges, review?.status, badgeGatingInput]
  );

  const {
    isHidden: isBadgeHidden,
    badgeStatus,
    reviewStatus: reviewStatusForDisplay,
    showSendForReview,
    showSummaryPanel,
  } = badgeDerivation;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [disableOptions, setDisableOptions] = useState(false);
  const [tooltipTitle, setTooltipTitle] = useState<string | null>(null);

  const onToggle = useCallback(
    (isOpen: boolean): void => {
      setIsDropdownOpen(isOpen);
      // Send-for-review validation reads live editor state (e.g. unsaved changes), so it must be
      // evaluated when the dropdown opens rather than at render time.
      if (isOpen && showSendForReview) {
        const validationError = getSendForReviewValidationError?.(reviewableId) ?? null;
        setDisableOptions(validationError != null);
        setTooltipTitle(validationError);
      }
    },
    [showSendForReview, getSendForReviewValidationError, reviewableId]
  );

  const contextValue: ReviewBadgeContextValue = useMemo(
    () => ({
      reviewableId,
      reviewable: accessibleReviewable,
      isDropdownOpen,
      setIsDropdownOpen,
      reviewError,
      onReviewChange: onReviewChangeWrapped,
      performanceMetadata,
      renderAdditionalReviewInfo,
      openHistory,
      reviewId,
      unsubmittedTableNames,
      openVersionId,
      isCurrentEditableDraft,
    }),
    [
      reviewableId,
      accessibleReviewable,
      isDropdownOpen,
      reviewError,
      onReviewChangeWrapped,
      performanceMetadata,
      renderAdditionalReviewInfo,
      openHistory,
      reviewId,
      unsubmittedTableNames,
      openVersionId,
      isCurrentEditableDraft,
    ]
  );

  const attachPublishState = useCallback(
    (
      controllerResult: ReviewControllerResult,
      queryState: 'pending' | 'error' | 'inaccessible' | 'ready'
    ): ReviewControllerResult => {
      if (cyclicalPublishGating == null) {
        return controllerResult;
      }

      return {
        ...controllerResult,
        publishState: deriveCyclicalPublishState({
          openVersionApiId: openVersionId,
          isLatest: cyclicalPublishGating.isLatest,
          isPublishOptionDisabledFromParent: cyclicalPublishGating.isPublishOptionDisabledFromParent,
          queryState,
          reviewable: accessibleReviewable,
        }),
      };
    },
    [accessibleReviewable, cyclicalPublishGating, openVersionId]
  );

  if (result.type === QueryResultType.PENDING) {
    return attachPublishState({state: 'PENDING'}, 'pending');
  }

  if (result.type === QueryResultType.ERROR) {
    return attachPublishState({state: 'HIDDEN', reason: 'query_failed'}, 'error');
  }

  if ((hasMismatchedCachedReviewable && isServingStaleCachedReviewable) || isRefetchingAfterReviewChange) {
    // Hide the badge while WHEN_LOADING serves stale cache (wrong reviewableId or pre-mutation status).
    return attachPublishState({state: 'PENDING'}, 'pending');
  }

  if (accessibleReviewable == null) {
    return attachPublishState({state: 'HIDDEN', reason: 'inaccessible'}, 'inaccessible');
  }

  if (isBadgeHidden) {
    return attachPublishState({state: 'HIDDEN', reason: 'badge_hidden'}, 'ready');
  }

  return attachPublishState(
    {
      state: 'READY',
      reviewStatus: reviewStatusForDisplay,
      terminalState: review?.activeReviewLifecycleVersion?.terminalState,
      badgeStatus,
      isDropdown: showSendForReview || showSummaryPanel,
      showSendForReview,
      showSummaryPanel,
      permissions: accessibleReviewable.permissions,
      contextValue,
      dropdown: {isOpen: isDropdownOpen, onToggle, disableOptions, tooltipTitle},
      realtime: {apiId: reviewableId, refetch: () => refetchCallback()},
    },
    'ready'
  );
}
